/**
 * 平台动态主题管理器 (Theme Manager)
 * 支持在管理后台动态配置门户顶部栏、门户选中主色、Scratch 主题色及 Scratch 扩展主题色，
 * 并通过 CSS 变量在全局实时无缝生效。
 */

export const DEFAULT_THEME = {
    THEME_PORTAL_HEADER_BG: '#FFFFFF',
    THEME_PORTAL_HEADER_TEXT: '#475569',
    THEME_PORTAL_PRIMARY_COLOR: '#0ea5e9',
    THEME_SCRATCH_PRIMARY_COLOR: '#855CD6',
    THEME_SCRATCH_EXTENSION_COLOR: '#855CD6'
};

// 辅助函数：将 Hex 颜色转为 RGB 分量字符串
const hexToRgb = (hex, defaultRgb = '14, 165, 233') => {
    if (!hex) return defaultRgb;
    let c = hex.replace('#', '').trim();
    if (c.length === 3) {
        c = c.split('').map(x => x + x).join('');
    }
    if (c.length === 6) {
        const num = parseInt(c, 16);
        return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
    }
    return defaultRgb;
};

/**
 * 将主题配置应用到 document.documentElement 的 CSS 变量
 * @param {object} config - 主题配置键值对
 */
export const applyThemeConfig = (config = {}) => {
    if (typeof document === 'undefined') return;

    const portalHeaderBg = config.THEME_PORTAL_HEADER_BG || 
                           window.localStorage.getItem('THEME_PORTAL_HEADER_BG') || 
                           DEFAULT_THEME.THEME_PORTAL_HEADER_BG;

    const portalHeaderText = config.THEME_PORTAL_HEADER_TEXT || 
                             window.localStorage.getItem('THEME_PORTAL_HEADER_TEXT') || 
                             DEFAULT_THEME.THEME_PORTAL_HEADER_TEXT;

    const portalPrimary = config.THEME_PORTAL_PRIMARY_COLOR || 
                          window.localStorage.getItem('THEME_PORTAL_PRIMARY_COLOR') || 
                          DEFAULT_THEME.THEME_PORTAL_PRIMARY_COLOR;

    const scratchPrimary = config.THEME_SCRATCH_PRIMARY_COLOR || 
                           window.localStorage.getItem('THEME_SCRATCH_PRIMARY_COLOR') || 
                           DEFAULT_THEME.THEME_SCRATCH_PRIMARY_COLOR;

    const scratchExt = config.THEME_SCRATCH_EXTENSION_COLOR || 
                       window.localStorage.getItem('THEME_SCRATCH_EXTENSION_COLOR') || 
                       DEFAULT_THEME.THEME_SCRATCH_EXTENSION_COLOR;

    const root = document.documentElement;

    // 1. 门户顶部栏与选中主题色
    root.style.setProperty('--theme-portal-header-bg', portalHeaderBg);
    root.style.setProperty('--theme-portal-header-text', portalHeaderText);
    root.style.setProperty('--theme-portal-primary', portalPrimary);
    root.style.setProperty('--theme-portal-primary-rgb', hexToRgb(portalPrimary, '14, 165, 233'));
    root.style.setProperty('--theme-portal-primary-soft', `rgba(${hexToRgb(portalPrimary, '14, 165, 233')}, 0.12)`);
    root.style.setProperty('--theme-portal-primary-hover', `rgba(${hexToRgb(portalPrimary, '14, 165, 233')}, 0.06)`);
    root.style.setProperty('--theme-portal-primary-glow', `rgba(${hexToRgb(portalPrimary, '14, 165, 233')}, 0.25)`);

    // 2. Scratch 编辑器主题色
    root.style.setProperty('--theme-scratch-primary', scratchPrimary);
    root.style.setProperty('--theme-scratch-primary-rgb', hexToRgb(scratchPrimary, '133, 92, 214'));
    root.style.setProperty('--theme-scratch-primary-soft', `rgba(${hexToRgb(scratchPrimary, '133, 92, 214')}, 0.15)`);
    root.style.setProperty('--theme-scratch-primary-glow', `rgba(${hexToRgb(scratchPrimary, '133, 92, 214')}, 0.35)`);

    // 3. Scratch 扩展中心主题色
    root.style.setProperty('--theme-scratch-extension', scratchExt);
    root.style.setProperty('--theme-scratch-extension-rgb', hexToRgb(scratchExt, '133, 92, 214'));
    root.style.setProperty('--theme-scratch-extension-soft', `rgba(${hexToRgb(scratchExt, '133, 92, 214')}, 0.18)`);
    root.style.setProperty('--theme-scratch-extension-header', scratchExt);
};

/**
 * 初始化全局主题（优先从 localStorage 毫秒级恢复，再通过 API 同步后台最新配置）
 */
export const initTheme = () => {
    // 立即应用缓存，防止页面白屏或样式闪烁
    applyThemeConfig();

    if (typeof window === 'undefined') return;

    // 异步拉取后台最新主题配置
    import('./platform-api').then(({ platformApi }) => {
        platformApi('/system/config', { method: 'GET' })
            .then(res => {
                if (res) {
                    if (res.THEME_PORTAL_HEADER_BG) window.localStorage.setItem('THEME_PORTAL_HEADER_BG', res.THEME_PORTAL_HEADER_BG);
                    if (res.THEME_PORTAL_HEADER_TEXT) window.localStorage.setItem('THEME_PORTAL_HEADER_TEXT', res.THEME_PORTAL_HEADER_TEXT);
                    if (res.THEME_PORTAL_PRIMARY_COLOR) window.localStorage.setItem('THEME_PORTAL_PRIMARY_COLOR', res.THEME_PORTAL_PRIMARY_COLOR);
                    if (res.THEME_SCRATCH_PRIMARY_COLOR) window.localStorage.setItem('THEME_SCRATCH_PRIMARY_COLOR', res.THEME_SCRATCH_PRIMARY_COLOR);
                    if (res.THEME_SCRATCH_EXTENSION_COLOR) window.localStorage.setItem('THEME_SCRATCH_EXTENSION_COLOR', res.THEME_SCRATCH_EXTENSION_COLOR);
                    applyThemeConfig(res);
                }
            })
            .catch(() => {});
    });
};
